import { useEffect, useState } from "react";
import api, { inr, formatApiError, API_BASE } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Download, FileJson, FileSpreadsheet, Info } from "lucide-react";
import { toast } from "sonner";

const monthName = (m) => {
  const [y, mo] = m.split("-");
  return new Date(y, Number(mo) - 1, 1).toLocaleDateString("en-IN", { month: "long", year: "numeric" });
};

export default function GSTReturns() {
  const today = new Date();
  const defaultMonth = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}`;
  const [month, setMonth] = useState(defaultMonth);
  const [tab, setTab] = useState("gstr1");
  const [r1, setR1] = useState(null);
  const [r3, setR3] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [a, b] = await Promise.all([
        api.get(`/gst/gstr1?month=${month}`),
        api.get(`/gst/gstr3b?month=${month}`),
      ]);
      setR1(a.data); setR3(b.data);
    } catch (e) { toast.error(formatApiError(e)); }
    setLoading(false);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const download = async (kind, fmt) => {
    try {
      const res = await api.get(`/gst/${kind}/download?month=${month}&format=${fmt}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${kind.toUpperCase()}-${month}.${fmt}`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Downloaded");
    } catch (e) {
      toast.error("Download failed");
    }
  };

  return (
    <div className="p-6 sm:p-8" data-testid="gst-returns-page">
      <div className="flex items-end justify-between flex-wrap gap-3 mb-6">
        <div>
          <h1 className="font-heading font-extrabold text-3xl text-[#2D2C2A]">GST Returns</h1>
          <p className="text-sm text-[#7A7873] mt-1">Auto-prepared GSTR-1 & GSTR-3B · download JSON for the GST offline tool or CSV for your CA.</p>
        </div>
        <div className="flex items-end gap-2">
          <div>
            <Label className="text-xs">Return Period</Label>
            <Input type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="h-10 border-[#EBE8E2]" data-testid="gst-month" />
          </div>
          <Button onClick={load} disabled={loading} className="bg-[#D95D39] hover:bg-[#C24F2C] h-10" data-testid="gst-run">
            {loading ? "Loading…" : "Generate"}
          </Button>
        </div>
      </div>

      <div className="bg-[#FFF6E8] border border-[#F4D58B] text-[#7A5A18] rounded-lg p-3 mb-6 text-sm flex gap-2">
        <Info size={16} className="mt-0.5 flex-shrink-0" />
        <div>
          Aggregated from all <strong>paid</strong> orders & <strong>non-cancelled</strong> purchase invoices for {monthName(month)}.
          The downloaded JSON matches the format expected by the <strong>GST Returns Offline Tool v3</strong>.
          Always reconcile with GSTR-2A before filing.
        </div>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="bg-white border border-[#EBE8E2]">
          <TabsTrigger value="gstr1" data-testid="tab-gstr1" className="data-[state=active]:bg-[#FBE9E2] data-[state=active]:text-[#D95D39]">
            GSTR-1 (Outward Supplies)
          </TabsTrigger>
          <TabsTrigger value="gstr3b" data-testid="tab-gstr3b" className="data-[state=active]:bg-[#FBE9E2] data-[state=active]:text-[#D95D39]">
            GSTR-3B (Summary)
          </TabsTrigger>
        </TabsList>

        {/* GSTR-1 */}
        <TabsContent value="gstr1" className="mt-4 space-y-4">
          {r1 && (
            <>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <Card className="p-4 border-[#EBE8E2] bg-white">
                  <div className="text-xs uppercase tracking-wider text-[#7A7873]">Orders</div>
                  <div className="font-heading font-extrabold text-2xl mt-1 font-mono-num" data-testid="r1-orders">{r1.order_count}</div>
                </Card>
                <Card className="p-4 border-[#EBE8E2] bg-white">
                  <div className="text-xs uppercase tracking-wider text-[#7A7873]">Taxable Value</div>
                  <div className="font-heading font-extrabold text-2xl mt-1 font-mono-num" data-testid="r1-taxable">{inr(r1.totals.taxable)}</div>
                </Card>
                <Card className="p-4 border-[#EBE8E2] bg-white">
                  <div className="text-xs uppercase tracking-wider text-[#7A7873]">CGST</div>
                  <div className="font-heading font-extrabold text-2xl mt-1 font-mono-num text-[#D95D39]">{inr(r1.totals.cgst)}</div>
                </Card>
                <Card className="p-4 border-[#EBE8E2] bg-white">
                  <div className="text-xs uppercase tracking-wider text-[#7A7873]">SGST</div>
                  <div className="font-heading font-extrabold text-2xl mt-1 font-mono-num text-[#D95D39]">{inr(r1.totals.sgst)}</div>
                </Card>
              </div>

              <Card className="p-5 border-[#EBE8E2] bg-white">
                <div className="flex items-baseline justify-between mb-3">
                  <h3 className="font-heading font-bold text-lg">B2CS — Supplies to unregistered (Place of Supply: {r1.state_code})</h3>
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline" onClick={() => download("gstr1", "csv")} data-testid="dl-r1-csv">
                      <FileSpreadsheet size={14} className="mr-1.5" /> CSV
                    </Button>
                    <Button size="sm" className="bg-[#D95D39] hover:bg-[#C24F2C]" onClick={() => download("gstr1", "json")} data-testid="dl-r1-json">
                      <FileJson size={14} className="mr-1.5" /> JSON
                    </Button>
                  </div>
                </div>
                <div className="overflow-x-auto">
                <table className="w-full text-sm min-w-[600px]">
                  <thead className="bg-[#F4F2EE] text-[#7A7873] text-xs uppercase tracking-wider">
                    <tr>
                      <th className="text-left px-3 py-2">Type</th>
                      <th className="text-right px-3 py-2">Rate</th>
                      <th className="text-right px-3 py-2">POS</th>
                      <th className="text-right px-3 py-2">Taxable Value</th>
                      <th className="text-right px-3 py-2">CGST</th>
                      <th className="text-right px-3 py-2">SGST</th>
                    </tr>
                  </thead>
                  <tbody>
                    {r1.b2cs.map((r, i) => (
                      <tr key={i} className="border-t border-[#EBE8E2]">
                        <td className="px-3 py-2">{r.sply_ty}</td>
                        <td className="px-3 py-2 text-right font-mono-num">{r.rt}%</td>
                        <td className="px-3 py-2 text-right font-mono-num">{r.pos}</td>
                        <td className="px-3 py-2 text-right font-mono-num font-semibold">{inr(r.txval)}</td>
                        <td className="px-3 py-2 text-right font-mono-num">{inr(r.camt)}</td>
                        <td className="px-3 py-2 text-right font-mono-num">{inr(r.samt)}</td>
                      </tr>
                    ))}
                    {r1.b2cs.length === 0 && (
                      <tr><td colSpan={6} className="px-3 py-8 text-center text-[#7A7873]">No outward supplies in this period.</td></tr>
                    )}
                  </tbody>
                </table>
                </div>
              </Card>

              <Card className="p-5 border-[#EBE8E2] bg-white">
                <h3 className="font-heading font-bold text-lg mb-3">HSN Summary</h3>
                <div className="overflow-x-auto">
                <table className="w-full text-sm min-w-[700px]">
                  <thead className="bg-[#F4F2EE] text-[#7A7873] text-xs uppercase tracking-wider">
                    <tr>
                      <th className="text-left px-3 py-2">HSN</th>
                      <th className="text-left px-3 py-2">Description</th>
                      <th className="text-right px-3 py-2">Qty</th>
                      <th className="text-right px-3 py-2">Rate</th>
                      <th className="text-right px-3 py-2">Taxable Value</th>
                      <th className="text-right px-3 py-2">CGST</th>
                      <th className="text-right px-3 py-2">SGST</th>
                    </tr>
                  </thead>
                  <tbody>
                    {r1.hsn.map((h, i) => (
                      <tr key={i} className="border-t border-[#EBE8E2]">
                        <td className="px-3 py-2 font-mono-num">{h.hsn_sc}</td>
                        <td className="px-3 py-2 text-[#7A7873]">{h.desc}</td>
                        <td className="px-3 py-2 text-right font-mono-num">{h.qty}</td>
                        <td className="px-3 py-2 text-right font-mono-num">{h.rt}%</td>
                        <td className="px-3 py-2 text-right font-mono-num">{inr(h.txval)}</td>
                        <td className="px-3 py-2 text-right font-mono-num">{inr(h.camt)}</td>
                        <td className="px-3 py-2 text-right font-mono-num">{inr(h.samt)}</td>
                      </tr>
                    ))}
                    {r1.hsn.length === 0 && (
                      <tr><td colSpan={7} className="px-3 py-8 text-center text-[#7A7873]">No HSN entries.</td></tr>
                    )}
                  </tbody>
                </table>
                </div>
              </Card>

              <Card className="p-5 border-[#EBE8E2] bg-white">
                <h3 className="font-heading font-bold text-lg mb-3">Document Summary</h3>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
                  <div><div className="text-xs text-[#7A7873] uppercase">Invoices</div><div className="font-mono-num font-semibold text-lg">{r1.doc_summary.count}</div></div>
                  <div><div className="text-xs text-[#7A7873] uppercase">Cancelled</div><div className="font-mono-num font-semibold text-lg">{r1.doc_summary.cancelled}</div></div>
                  <div><div className="text-xs text-[#7A7873] uppercase">From</div><div className="font-mono-num text-sm">{r1.doc_summary.from || "—"}</div></div>
                  <div><div className="text-xs text-[#7A7873] uppercase">To</div><div className="font-mono-num text-sm">{r1.doc_summary.to || "—"}</div></div>
                </div>
              </Card>
            </>
          )}
        </TabsContent>

        {/* GSTR-3B */}
        <TabsContent value="gstr3b" className="mt-4 space-y-4">
          {r3 && (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <Card className="p-4 border-[#EBE8E2] bg-white">
                  <div className="text-xs uppercase tracking-wider text-[#7A7873]">Output Tax (Sales)</div>
                  <div className="font-heading font-extrabold text-2xl mt-1 font-mono-num text-[#D95D39]" data-testid="r3-output">{inr(r3.outward.total_tax)}</div>
                  <div className="text-xs text-[#7A7873] mt-1 font-mono-num">{inr(r3.outward.taxable)} taxable · {r3.outward.order_count} orders</div>
                </Card>
                <Card className="p-4 border-[#EBE8E2] bg-white">
                  <div className="text-xs uppercase tracking-wider text-[#7A7873]">Input Tax Credit (Purchases)</div>
                  <div className="font-heading font-extrabold text-2xl mt-1 font-mono-num text-[#6A7F66]" data-testid="r3-itc">{inr(r3.itc.total)}</div>
                  <div className="text-xs text-[#7A7873] mt-1 font-mono-num">{inr(r3.itc.taxable)} taxable · {r3.itc.purchase_count} purchases</div>
                </Card>
                <Card className="p-4 border-[#EBE8E2] bg-white">
                  <div className="text-xs uppercase tracking-wider text-[#7A7873]">Net Tax Payable</div>
                  <div className="font-heading font-extrabold text-2xl mt-1 font-mono-num" style={{ color: r3.net_payable.total > 0 ? "#D95D39" : "#6A7F66" }} data-testid="r3-net">
                    {inr(Math.max(0, r3.net_payable.total))}
                  </div>
                  <div className="text-xs text-[#7A7873] mt-1 font-mono-num">
                    {r3.net_payable.total <= 0 ? "Credit balance · carry forward" : "To pay before filing"}
                  </div>
                </Card>
              </div>

              <Card className="p-5 border-[#EBE8E2] bg-white">
                <div className="flex items-baseline justify-between mb-3">
                  <h3 className="font-heading font-bold text-lg">GSTR-3B Summary · {monthName(r3.month)}</h3>
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline" onClick={() => download("gstr3b", "csv")} data-testid="dl-r3-csv">
                      <FileSpreadsheet size={14} className="mr-1.5" /> CSV
                    </Button>
                    <Button size="sm" className="bg-[#D95D39] hover:bg-[#C24F2C]" onClick={() => download("gstr3b", "json")} data-testid="dl-r3-json">
                      <FileJson size={14} className="mr-1.5" /> JSON
                    </Button>
                  </div>
                </div>
                <div className="overflow-x-auto">
                <table className="w-full text-sm min-w-[680px]">
                  <thead className="bg-[#F4F2EE] text-[#7A7873] text-xs uppercase tracking-wider">
                    <tr>
                      <th className="text-left px-3 py-2">Section</th>
                      <th className="text-left px-3 py-2">Description</th>
                      <th className="text-right px-3 py-2">Taxable Value</th>
                      <th className="text-right px-3 py-2">CGST</th>
                      <th className="text-right px-3 py-2">SGST</th>
                      <th className="text-right px-3 py-2">Total Tax</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="border-t border-[#EBE8E2]">
                      <td className="px-3 py-2 font-mono-num font-semibold">3.1(a)</td>
                      <td className="px-3 py-2">Outward taxable supplies (other than zero-rated, nil-rated and exempted)</td>
                      <td className="px-3 py-2 text-right font-mono-num">{inr(r3.outward.taxable)}</td>
                      <td className="px-3 py-2 text-right font-mono-num">{inr(r3.outward.cgst)}</td>
                      <td className="px-3 py-2 text-right font-mono-num">{inr(r3.outward.sgst)}</td>
                      <td className="px-3 py-2 text-right font-mono-num font-semibold">{inr(r3.outward.total_tax)}</td>
                    </tr>
                    <tr className="border-t border-[#EBE8E2] bg-[#FBFAF8]">
                      <td className="px-3 py-2 font-mono-num font-semibold">4(A)(5)</td>
                      <td className="px-3 py-2">All other ITC (from purchase invoices)</td>
                      <td className="px-3 py-2 text-right font-mono-num">{inr(r3.itc.taxable)}</td>
                      <td className="px-3 py-2 text-right font-mono-num text-[#6A7F66]">{inr(r3.itc.cgst)}</td>
                      <td className="px-3 py-2 text-right font-mono-num text-[#6A7F66]">{inr(r3.itc.sgst)}</td>
                      <td className="px-3 py-2 text-right font-mono-num font-semibold text-[#6A7F66]">{inr(r3.itc.total)}</td>
                    </tr>
                    <tr className="border-t-2 border-[#2D2C2A] bg-[#FBE9E2]">
                      <td className="px-3 py-2.5 font-semibold" colSpan={2}>Net Tax Payable (Output − ITC)</td>
                      <td className="px-3 py-2.5"></td>
                      <td className="px-3 py-2.5 text-right font-mono-num font-bold">{inr(r3.net_payable.cgst)}</td>
                      <td className="px-3 py-2.5 text-right font-mono-num font-bold">{inr(r3.net_payable.sgst)}</td>
                      <td className="px-3 py-2.5 text-right font-mono-num font-extrabold text-lg" style={{ color: r3.net_payable.total > 0 ? "#D95D39" : "#6A7F66" }}>
                        {inr(r3.net_payable.total)}
                      </td>
                    </tr>
                  </tbody>
                </table>
                </div>
              </Card>
            </>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
